import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { BrowserRouter as Router } from 'react-router-dom'
import Favicon from 'react-favicon'
import TagManager from 'react-gtm-module'
import CssBaseline from '@material-ui/core/CssBaseline'
import { ThemeProvider } from '@material-ui/core/styles'
import { storeBrandActionCreator } from './redux/actions/actions'
import { getLanguageLocale, persistLocale } from './utils/utils'
import { getBrandConfiguration, getCaptchaKeyService } from './services/services'
import { DataProvider } from './context/DataProvider'
import LanguageProvider from './components/language-provider'
import LangBar from './components/bars/header/lang-bar/lang-bar'
import Landing from './pages/landing'
import NoMatch from './pages/no-match/no-match'
import Theme from './components/theme/theme'
import { DEFAULT_LOCALE } from './i18n'

const AppContent = () => {
  const theme = Theme()
  const dispatch = useDispatch()
  const [captchaKey, setCaptchaKey] = useState('')
  const [captchaLoaded, setCaptchaLoaded] = useState(false)
  const [brandLoaded, setBrandLoaded] = useState(false)
  const [brand, setBrand] = useState(null)
  const [brandError, setBrandError] = useState(false)
  const [favicon, setFavicon] = useState()
  const [currentLocale, setCurrentLocale] = useState(getLanguageLocale(DEFAULT_LOCALE))

  const captchaKeyCallback = (response) => {
    setCaptchaLoaded(true)
    setCaptchaKey(response?.data?.siteKey)
  }

  const captchaKeyErrorCallback = (err) => {
    setCaptchaLoaded(true)
    console.error(err)
  }

  useEffect(() => {
    getCaptchaKeyService(captchaKeyCallback, captchaKeyErrorCallback)
  }, [])

  const onChangeLanguage = (value) => {
    setCurrentLocale(value)
    persistLocale(value)
  }

  const brandConfigurationCallback = (response) => {
    setBrandLoaded(true)
    setFavicon(response?.data?.imageLogos.favicon)
    dispatch(storeBrandActionCreator(response.data))
    setBrand(response?.data)

    if (response?.data?.googleTagManagerId) {
      TagManager.initialize({ gtmId: response.data.googleTagManagerId })
    }
  }

  const brandConfigurationErrorCallback = (err) => {
    setBrandLoaded(true)
    setBrandError(true)
    console.error(err)
  }

  useEffect(() => {
    getBrandConfiguration(brandConfigurationCallback, brandConfigurationErrorCallback)
  }, [])

  // El idioma puede venir de la cookie del portal de empresa o de OAuth, con localStorage vacío en este dominio
  useEffect(() => {
    if (brand && Object.keys(brand).length > 0) {
      const resolvedLocale = getLanguageLocale(brand)
      setCurrentLocale(resolvedLocale)
      persistLocale(resolvedLocale)
    }
  }, [brand])

  // El banner de cookies toma su idioma del atributo lang del html: hay que mantenerlo
  // sincronizado con el idioma activo o se muestra en ingles
  useEffect(() => {
    if (document.documentElement.lang !== currentLocale) {
      document.documentElement.lang = currentLocale
    }
  }, [currentLocale])

  if (!brandLoaded || !captchaLoaded) return null

  // Sin site key no se puede proteger la web con reCAPTCHA: se muestra la pagina de error
  const configurationError = brandError || !brand || !captchaKey

  return (
    <>
      {favicon && <Favicon url={favicon} />}
      <Router>
        <DataProvider languageSetter={onChangeLanguage}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LanguageProvider locale={currentLocale}>
              {configurationError ? (
                <NoMatch />
              ) : (
                <GoogleReCaptchaProvider reCaptchaKey={captchaKey}>
                  <div>
                    <LangBar onChangeLanguage={onChangeLanguage} />
                    <Landing locale={currentLocale} />
                  </div>
                </GoogleReCaptchaProvider>
              )}
            </LanguageProvider>
          </ThemeProvider>
        </DataProvider>
      </Router>
    </>
  )
}

export default AppContent
